import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Camera Obstruction Detection Hook
 * 
 * Detects various types of camera obstruction including:
 * - Partial/full camera covering
 * - Phone being held near face
 * - Camera being turned away
 * - Dark patches and shapes
 * 
 * Features progressive severity levels and false positive prevention
 */

export interface ObstructionStatus {
  isObstructed: boolean;
  obstructionType: ObstructionType | null;
  severity: ObstructionSeverity;
  confidence: number; // 0-1
  duration: number; // milliseconds
  metrics: ObstructionMetrics;
}

export type ObstructionType = 
  | 'camera_partially_covered'
  | 'camera_fully_covered'
  | 'phone_detected_near_face'
  | 'camera_turned_away'
  | 'dark_object_blocking'
  | 'none';

export type ObstructionSeverity = 'none' | 'warning' | 'flag' | 'critical';

export interface ObstructionMetrics {
  imageEntropy: number; // 0-8 (bits)
  variance: number; // pixel variance
  darkPatchRatio: number; // 0-1
  rectangularShapes: number; // count
  brightnessChange: number; // -1 to 1
  backgroundStability: number; // 0-1
  handNearFace: boolean;
  screenGlowDetected: boolean;
}

interface ObstructionConfig {
  // Thresholds
  lowEntropyThreshold: number; // < 3.5 bits = suspicious
  darkPatchThreshold: number; // > 0.3 ratio = suspicious
  brightnessDropThreshold: number; // > 0.4 change = suspicious
  backgroundChangeThreshold: number; // < 0.7 stability = suspicious
  
  // Timing (ms)
  warningDuration: number; // 2000ms
  flagDuration: number; // 5000ms
  criticalDuration: number; // 10000ms
  
  // False positive prevention
  allowGlassesFrames: boolean;
  allowHandTouchFace: boolean;
  blinkIgnoreTime: number; // 500ms
  
  // Detection sensitivity
  phoneDetectionSensitivity: number; // 0-1
  shapeDetectionEnabled: boolean;
}

const DEFAULT_CONFIG: ObstructionConfig = {
  lowEntropyThreshold: 3.5,
  darkPatchThreshold: 0.3,
  brightnessDropThreshold: 0.4,
  backgroundChangeThreshold: 0.7,
  
  warningDuration: 2000,
  flagDuration: 5000,
  criticalDuration: 10000,
  
  allowGlassesFrames: true,
  allowHandTouchFace: true,
  blinkIgnoreTime: 500,
  
  phoneDetectionSensitivity: 0.7,
  shapeDetectionEnabled: true,
};

export function useCameraObstruction(
  videoRef: React.RefObject<HTMLVideoElement>,
  enabled: boolean = true,
  config: Partial<ObstructionConfig> = {}
): ObstructionStatus {
  
  const fullConfig = { ...DEFAULT_CONFIG, ...config };
  
  const [obstructionStatus, setObstructionStatus] = useState<ObstructionStatus>({
    isObstructed: false,
    obstructionType: null,
    severity: 'none',
    confidence: 0,
    duration: 0,
    metrics: {
      imageEntropy: 0,
      variance: 0,
      darkPatchRatio: 0,
      rectangularShapes: 0,
      brightnessChange: 0,
      backgroundStability: 1,
      handNearFace: false,
      screenGlowDetected: false,
    }
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const previousFrameRef = useRef<ImageData | null>(null);
  const backgroundHistoryRef = useRef<number[]>([]);
  const obstructionStartTimeRef = useRef<number | null>(null);
  const lastBlinkTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number>();

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
  }, []);

  /**
   * Calculate image entropy (Shannon entropy)
   * Low entropy = uniform/covered image
   */
  const calculateEntropy = useCallback((data: Uint8ClampedArray): number => {
    const histogram = new Array(256).fill(0);
    const totalPixels = data.length / 4;

    // Build histogram from grayscale values
    for (let i = 0; i < data.length; i += 4) {
      const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
      histogram[gray]++;
    }

    // Calculate entropy
    let entropy = 0;
    for (let i = 0; i < 256; i++) {
      if (histogram[i] > 0) {
        const probability = histogram[i] / totalPixels;
        entropy -= probability * Math.log2(probability);
      }
    }

    return entropy;
  }, []);

  /**
   * Calculate image variance
   * Low variance = uniform/covered image
   */
  const calculateVariance = useCallback((data: Uint8ClampedArray): number => {
    let sum = 0;
    let sumSq = 0;
    const totalPixels = data.length / 4;

    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      sum += gray;
      sumSq += gray * gray;
    }

    const mean = sum / totalPixels;
    const variance = (sumSq / totalPixels) - (mean * mean);
    
    return variance;
  }, []);

  /**
   * Detect dark patches in the frame
   * Returns ratio of dark pixels to total pixels
   */
  const detectDarkPatches = useCallback((
    data: Uint8ClampedArray,
    width: number,
    height: number
  ): { ratio: number; shapes: number } => {
    const darkThreshold = 50; // pixel value < 50 = dark
    const patchSize = 20; // pixels
    let darkPixels = 0;
    const darkMap: boolean[][] = [];

    // Initialize dark map
    for (let y = 0; y < height; y++) {
      darkMap[y] = [];
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const gray = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
        const isDark = gray < darkThreshold;
        darkMap[y][x] = isDark;
        if (isDark) darkPixels++;
      }
    }

    const ratio = darkPixels / (width * height);

    // Detect rectangular/circular shapes (simplified)
    let shapeCount = 0;
    if (fullConfig.shapeDetectionEnabled) {
      shapeCount = detectRectangularShapes(darkMap, width, height, patchSize);
    }

    return { ratio, shapes: shapeCount };
  }, [fullConfig.shapeDetectionEnabled]);

  /**
   * Detect rectangular dark shapes (phone-like objects)
   */
  const detectRectangularShapes = useCallback((
    darkMap: boolean[][],
    width: number,
    height: number,
    minSize: number
  ): number => {
    let shapeCount = 0;
    const visited: boolean[][] = Array(height).fill(null).map(() => Array(width).fill(false));

    // Simple connected component analysis
    for (let y = 0; y < height - minSize; y += minSize) {
      for (let x = 0; x < width - minSize; x += minSize) {
        if (darkMap[y][x] && !visited[y][x]) {
          const componentSize = floodFill(darkMap, visited, x, y, width, height);
          
          // If component is large enough, consider it a shape
          if (componentSize > minSize * minSize) {
            shapeCount++;
          }
        }
      }
    }

    return shapeCount;
  }, []);

  /**
   * Flood fill for connected component detection
   */
  const floodFill = (
    darkMap: boolean[][],
    visited: boolean[][],
    x: number,
    y: number,
    width: number,
    height: number
  ): number => {
    if (x < 0 || x >= width || y < 0 || y >= height) return 0;
    if (visited[y][x] || !darkMap[y][x]) return 0;

    visited[y][x] = true;
    let size = 1;

    // Check 4 neighbors (simplified, not full flood fill for performance)
    const neighbors = [[0, 1], [1, 0], [0, -1], [-1, 0]];
    for (const [dx, dy] of neighbors) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && nx < width && ny >= 0 && ny < height && !visited[ny][nx] && darkMap[ny][nx]) {
        size++;
        visited[ny][nx] = true;
      }
    }

    return size;
  };

  /**
   * Detect screen glow (bright rectangular area in dark environment)
   */
  const detectScreenGlow = useCallback((
    data: Uint8ClampedArray,
    width: number,
    height: number
  ): boolean => {
    const centerX = Math.floor(width / 2);
    const centerY = Math.floor(height / 2);
    const sampleSize = 30;

    // Sample center area brightness
    let centerBrightness = 0;
    let edgeBrightness = 0;
    let centerSamples = 0;
    let edgeSamples = 0;

    for (let y = 0; y < height; y += 5) {
      for (let x = 0; x < width; x += 5) {
        const idx = (y * width + x) * 4;
        const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;

        const distFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        
        if (distFromCenter < sampleSize) {
          centerBrightness += brightness;
          centerSamples++;
        } else if (distFromCenter > Math.min(width, height) / 3) {
          edgeBrightness += brightness;
          edgeSamples++;
        }
      }
    }

    if (centerSamples === 0 || edgeSamples === 0) return false;

    const avgCenterBrightness = centerBrightness / centerSamples;
    const avgEdgeBrightness = edgeBrightness / edgeSamples;

    // Screen glow: center much brighter than edges in dark environment
    const glowRatio = avgCenterBrightness / (avgEdgeBrightness + 1);
    return avgEdgeBrightness < 80 && glowRatio > 2.5;
  }, []);

  /**
   * Calculate background stability
   * Compares current frame to previous frames
   */
  const calculateBackgroundStability = useCallback((
    currentData: Uint8ClampedArray,
    previousData: Uint8ClampedArray | null
  ): number => {
    if (!previousData) return 1.0;

    let totalDiff = 0;
    const sampleRate = 10; // Sample every 10th pixel for performance

    for (let i = 0; i < currentData.length; i += 4 * sampleRate) {
      const currentGray = 0.299 * currentData[i] + 0.587 * currentData[i + 1] + 0.114 * currentData[i + 2];
      const previousGray = 0.299 * previousData[i] + 0.587 * previousData[i + 1] + 0.114 * previousData[i + 2];
      totalDiff += Math.abs(currentGray - previousGray);
    }

    const avgDiff = totalDiff / (currentData.length / (4 * sampleRate));
    const stability = Math.max(0, 1 - (avgDiff / 255));

    return stability;
  }, []);

  /**
   * Detect hand near face (simplified)
   * Looks for skin-colored regions in face area
   */
  const detectHandNearFace = useCallback((
    data: Uint8ClampedArray,
    width: number,
    height: number
  ): boolean => {
    if (!fullConfig.allowHandTouchFace) return false;

    const centerX = Math.floor(width / 2);
    const centerY = Math.floor(height / 2);
    const radius = Math.min(width, height) / 4;

    let skinPixels = 0;
    let totalSampled = 0;

    // Sample face area
    for (let y = centerY - radius; y < centerY + radius; y += 3) {
      for (let x = centerX - radius; x < centerX + radius; x += 3) {
        if (x < 0 || x >= width || y < 0 || y >= height) continue;

        const idx = (y * width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];

        // Simple skin detection (RGB ranges)
        const isSkin = r > 95 && g > 40 && b > 20 &&
                       r > g && r > b &&
                       Math.abs(r - g) > 15;

        if (isSkin) skinPixels++;
        totalSampled++;
      }
    }

    const skinRatio = skinPixels / totalSampled;
    return skinRatio > 0.4; // High skin ratio might indicate hand
  }, [fullConfig.allowHandTouchFace]);

  /**
   * Main analysis function
   */
  const analyzeFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!enabled || !video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      return;
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
    if (!ctx) return;

    // Set canvas size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Calculate metrics
    const entropy = calculateEntropy(data);
    const variance = calculateVariance(data);
    const { ratio: darkPatchRatio, shapes: rectangularShapes } = detectDarkPatches(
      data,
      canvas.width,
      canvas.height
    );
    const screenGlow = detectScreenGlow(data, canvas.width, canvas.height);
    const backgroundStability = calculateBackgroundStability(
      data,
      previousFrameRef.current?.data || null
    );
    const handNearFace = detectHandNearFace(data, canvas.width, canvas.height);

    // Calculate brightness change
    const currentBrightness = Array.from(data).reduce((sum: number, val: number, i: number) => 
      i % 4 < 3 ? sum + val : sum, 0) / (data.length * 3 / 4);
    
    backgroundHistoryRef.current.push(currentBrightness);
    if (backgroundHistoryRef.current.length > 30) {
      backgroundHistoryRef.current.shift();
    }

    const avgHistoricalBrightness = backgroundHistoryRef.current.length > 1
      ? backgroundHistoryRef.current.slice(0, -1).reduce((a, b) => a + b, 0) / (backgroundHistoryRef.current.length - 1)
      : currentBrightness;

    const brightnessChange = Math.abs(currentBrightness - avgHistoricalBrightness) / 255;

    // Store current frame for next comparison
    previousFrameRef.current = imageData;

    // Determine obstruction type and confidence
    let obstructionType: ObstructionType = 'none';
    let confidence = 0;
    let isObstructed = false;

    // Check for full camera covering (very low entropy + high dark patch ratio)
    if (entropy < fullConfig.lowEntropyThreshold && darkPatchRatio > fullConfig.darkPatchThreshold) {
      obstructionType = 'camera_fully_covered';
      confidence = Math.min(1, (fullConfig.darkPatchThreshold / (darkPatchRatio + 0.01)) * 
                               (fullConfig.lowEntropyThreshold / (entropy + 0.01)));
      isObstructed = true;
    }
    // Check for partial covering (low variance + some dark patches)
    else if (variance < 500 && darkPatchRatio > 0.15) {
      obstructionType = 'camera_partially_covered';
      confidence = Math.min(1, darkPatchRatio * 2);
      isObstructed = true;
    }
    // Check for phone near face (rectangular shapes + screen glow)
    else if (rectangularShapes > 0 && screenGlow) {
      obstructionType = 'phone_detected_near_face';
      confidence = fullConfig.phoneDetectionSensitivity;
      isObstructed = true;
    }
    // Check for dark object blocking
    else if (rectangularShapes > 0 && darkPatchRatio > 0.2) {
      obstructionType = 'dark_object_blocking';
      confidence = Math.min(1, darkPatchRatio * rectangularShapes * 0.5);
      isObstructed = true;
    }
    // Check for camera turned away (background change)
    else if (backgroundStability < fullConfig.backgroundChangeThreshold && 
             brightnessChange > fullConfig.brightnessDropThreshold) {
      obstructionType = 'camera_turned_away';
      confidence = Math.min(1, brightnessChange * (1 - backgroundStability));
      isObstructed = true;
    }

    // False positive prevention
    const now = Date.now();
    
    // Ignore brief blinks
    if (obstructionType === 'camera_partially_covered' && 
        now - lastBlinkTimeRef.current < fullConfig.blinkIgnoreTime) {
      isObstructed = false;
      obstructionType = 'none';
      confidence = 0;
    }

    // Ignore hand touching face briefly
    if (handNearFace && fullConfig.allowHandTouchFace) {
      if (obstructionType === 'camera_partially_covered' || 
          obstructionType === 'dark_object_blocking') {
        confidence *= 0.5; // Reduce confidence
      }
    }

    // Track obstruction duration
    let duration = 0;
    if (isObstructed) {
      if (!obstructionStartTimeRef.current) {
        obstructionStartTimeRef.current = now;
      }
      duration = now - obstructionStartTimeRef.current;
    } else {
      obstructionStartTimeRef.current = null;
      lastBlinkTimeRef.current = now;
    }

    // Determine severity based on duration and type
    let severity: ObstructionSeverity = 'none';
    if (isObstructed) {
      if (obstructionType === 'camera_fully_covered' && duration > fullConfig.flagDuration) {
        severity = 'critical';
      } else if (duration > fullConfig.criticalDuration) {
        severity = 'critical';
      } else if (duration > fullConfig.flagDuration) {
        severity = 'flag';
      } else if (duration > fullConfig.warningDuration) {
        severity = 'flag';
      } else {
        severity = 'warning';
      }
    }

    // Update state
    setObstructionStatus({
      isObstructed,
      obstructionType,
      severity,
      confidence,
      duration,
      metrics: {
        imageEntropy: entropy,
        variance,
        darkPatchRatio,
        rectangularShapes,
        brightnessChange,
        backgroundStability,
        handNearFace,
        screenGlowDetected: screenGlow,
      }
    });

  }, [
    enabled,
    videoRef,
    calculateEntropy,
    calculateVariance,
    detectDarkPatches,
    detectScreenGlow,
    calculateBackgroundStability,
    detectHandNearFace,
    fullConfig
  ]);

  // Animation loop
  useEffect(() => {
    if (!enabled) return;

    const loop = () => {
      analyzeFrame();
      animationFrameRef.current = requestAnimationFrame(loop);
    };

    animationFrameRef.current = requestAnimationFrame(loop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [enabled, analyzeFrame]);

  return obstructionStatus;
}
